/**
 * Accessibility utilities and hooks for consistent a11y patterns
 */

import { useEffect, useRef } from "react";

/**
 * Hook to manage focus for modals and dialogs
 */
export function useFocusManagement(isOpen: boolean) {
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement;
    } else {
      // Restore focus when modal closes
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
        previousActiveElement.current = null;
      }
    }
  }, [isOpen]);
}

/**
 * Hook to trap focus within a container (for modals)
 */
export function useFocusTrap(isActive: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    // Focus the first element when trap becomes active
    firstElement?.focus();

    document.addEventListener("keydown", handleTabKey);
    return () => document.removeEventListener("keydown", handleTabKey);
  }, [isActive]);

  return containerRef;
}

/**
 * Hook to announce changes to screen readers
 */
export function useAriaLive() {
  const announce = (message: string, priority: "polite" | "assertive" = "polite") => {
    const announcement = document.createElement("div");
    announcement.setAttribute("aria-live", priority);
    announcement.setAttribute("aria-atomic", "true");
    announcement.className = "sr-only";
    announcement.textContent = message;

    document.body.appendChild(announcement);

    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  return announce;
}

/**
 * Generate accessible IDs for form elements
 */
export function useAccessibleId(prefix: string) {
  const idRef = useRef<string>(`${prefix}-${Math.random().toString(36).substr(2, 9)}`);
  return idRef.current;
}

/**
 * Common ARIA attributes for consistent accessibility
 */
export const ariaAttributes = {
  button: {
    role: "button",
    tabIndex: 0,
  },
  modal: {
    role: "dialog",
    "aria-modal": true,
    "aria-hidden": false,
  },
  navigation: {
    role: "navigation",
    "aria-label": "Main navigation",
  },
  form: {
    role: "form",
    "aria-label": "Contact form",
  },
  toggle: {
    role: "switch",
    "aria-checked": false,
  },
} as const;
